import { z } from 'zod';

/**
 * Every HTTP error the API can answer, with its status. The wire format is always
 * `{ error: { code, message, details?, requestId? } }` (see ApiErrorBodyZ).
 * Messages are human and short ("That code's expired. Ask the owner for a fresh one.").
 */
export const ERROR_STATUS = {
  VALIDATION: 400,
  INVALID_CODE: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NO_CONSENT: 403,
  NOT_KEEPER: 403,
  NOT_FOUND: 404,
  MCP_DISABLED: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  GAME_LOCKED: 409,
  ALREADY_DONE: 409,
  GONE: 410,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA: 415,
  CLIENT_OUTDATED: 426,
  INSTALL_REQUIRED: 428,
  RATE_LIMITED: 429,
  DAILY_LIMIT: 429,
  BUDGET_CAP: 429,
  INTERNAL: 500,
  NOT_IMPLEMENTED: 501,
  UNAVAILABLE: 503,
  AI_DISABLED: 503,
  PUSH_DISABLED: 503,
} as const;

export type ErrorCode = keyof typeof ERROR_STATUS;
export const ERROR_CODES = Object.keys(ERROR_STATUS) as [ErrorCode, ...ErrorCode[]];
export const ErrorCodeZ = z.enum(ERROR_CODES);

export function statusFor(code: ErrorCode): number {
  return ERROR_STATUS[code];
}

export const ApiErrorBodyZ = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    requestId: z.string().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBodyZ>;

/**
 * Thrown by the typed client on any non-2xx answer, and thrown by server handlers to
 * answer one (core maps it to the wire format). `code` is an ErrorCode for answers from
 * our own server; an unknown code from a proxy keeps the raw string.
 */
export class ApiError extends Error {
  readonly code: ErrorCode | (string & {});
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(
    code: ErrorCode | (string & {}),
    message: string,
    opts: { status?: number; details?: unknown; requestId?: string } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = opts.status ?? (code in ERROR_STATUS ? ERROR_STATUS[code as ErrorCode] : 500);
    this.details = opts.details;
    this.requestId = opts.requestId;
  }

  toBody(): ApiErrorBody {
    const error: ApiErrorBody['error'] = { code: this.code, message: this.message };
    if (this.details !== undefined) error.details = this.details;
    if (this.requestId !== undefined) error.requestId = this.requestId;
    return { error };
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Plain result type for pure functions that can fail without throwing. */
export type Result<T, E = ApiError> = { ok: true; value: T } | { ok: false; error: E };
export const ok = <T>(value: T): Result<T, never> => ({ ok: true, value });
export const err = <E>(error: E): Result<never, E> => ({ ok: false, error });

/**
 * Per-item rejection codes inside a batch response (R4). These are lowercase on purpose:
 * they are not HTTP errors, the batch itself answers 200.
 */
export const GAME_EVENT_REJECT_CODES = [
  'game_locked',
  'not_keeper',
  'bad_target',
  'player_not_in_game',
  'invalid',
] as const;
export type GameEventRejectCode = (typeof GAME_EVENT_REJECT_CODES)[number];

export const DRILL_ATTEMPT_REJECT_CODES = ['session_locked', 'player_not_in_session', 'invalid'] as const;
export type DrillAttemptRejectCode = (typeof DRILL_ATTEMPT_REJECT_CODES)[number];

export const LAB_EVENT_REJECT_CODES = [
  'run_locked',
  'not_keeper',
  'bad_target',
  'player_not_in_run',
  'invalid',
  'action_rejected',
  'step_limit',
  'event_limit',
] as const;
export type LabEventRejectCode = (typeof LAB_EVENT_REJECT_CODES)[number];
